<script setup lang="ts">
/**
 * Confirmation Dialog Component
 *
 * Modal dialog for confirming actions
 */
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import { AlertTriangleIcon, InfoIcon } from 'lucide-vue-next';
import Button from 'primevue/button';

const { confirmState, handleConfirm, handleCancel } = useConfirm();

const iconMap = {
  danger: AlertTriangleIcon,
  warning: AlertTriangleIcon,
  info: InfoIcon,
};

const iconColorMap = {
  danger: 'text-danger-600',
  warning: 'text-yellow-600',
  info: 'text-blue-600',
};

const bgColorMap = {
  danger: 'bg-danger-100',
  warning: 'bg-yellow-100',
  info: 'bg-blue-100',
};
</script>

<template>
  <TransitionRoot
    as="template"
    :show="confirmState.isOpen"
  >
    <Dialog
      as="div"
      class="relative z-50"
      @close="handleCancel"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10"
                  :class="bgColorMap[confirmState.type || 'info']"
                >
                  <component
                    :is="iconMap[confirmState.type || 'info']"
                    class="h-6 w-6"
                    :class="iconColorMap[confirmState.type || 'info']"
                    aria-hidden="true"
                  />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6 text-gray-900"
                  >
                    {{ confirmState.title }}
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      {{ confirmState.message }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
                <Button
                  type="button"
                  :severity="confirmState.type === 'danger' ? 'danger' : 'primary'"
                  class="w-full! sm:w-auto!"
                  :label="confirmState.confirmText"
                  @click="handleConfirm"
                />
                <Button
                  type="button"
                  severity="secondary"
                  outlined
                  class="w-full! sm:w-auto!"
                  :label="confirmState.cancelText"
                  @click="handleCancel"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
